import {
  AudioLinesIcon,
  BookOpenIcon,
  BoxesIcon,
  BrainCircuitIcon,
  ChartLineIcon,
  ClipboardCheckIcon,
  DatabaseIcon,
  FileCheck2Icon,
  FilePenLineIcon,
  FileStackIcon,
  FileTextIcon,
  FlaskConicalIcon,
  GraduationCapIcon,
  HistoryIcon,
  LayersIcon,
  LinkIcon,
  ArrowRightLeftIcon,
  ClipboardListIcon,
  Grid3x3Icon,
  MessageSquareTextIcon,
  MonitorXIcon,
  NetworkIcon,
  PaletteIcon,
  ReceiptIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  UsersIcon,
  VariableIcon,
  SettingsIcon,
  VoicemailIcon,
  WorkflowIcon,
  HeadphonesIcon,
} from 'lucide-react';

import type { RouteConfig } from '@/shared/lib/ts-router';
import { AppLayout } from '@/app/layouts/AppLayout/AppLayout';
import { RouteErrorElement } from '@/app/providers/AppRouter/RouteErrorElement';
import { Icon } from '@/shared/ui/Icon/Icon';

import { AttributesPage } from '@/pages/attributes';
import { AttributeGroupsPage } from '@/pages/attributeGroups';
import { AudioAuditPage } from '@/pages/audio-audit';
import { BehaviorFactorsPage } from '@/pages/behavior-factors';
import { BetaFeaturesPage } from '@/pages/beta-features';
import { BillingPage } from '@/pages/billing';
import { CoachingPage } from '@/pages/coaching';
import { ExpertReportPage } from '@/pages/expert-report';
import { ExpertReportFeedbackPage } from '@/pages/expert-report-feedback';
import { GeneralSettingsPage } from '@/pages/general-settings';
import { QualityDashboardPage } from '@/pages/quality-dashboard';
import { RenessansDashboardPage } from '@/pages/renessans-dashboard';
import { MissellingDashboardPage } from '@/pages/misselling-dashboard';
import { StrategistHierarchyPage } from '@/pages/strategist-hierarchy';
import { StrategistMatrixPage } from '@/pages/strategist-matrix';
import { StrategistStandardsPage } from '@/pages/strategist-standards';
import { StrategistTransitionsPage } from '@/pages/strategist-transitions';
// import { DialogsPage } from '@/pages/dialogs';
import { DialogsBetaPage } from '@/pages/dialogs-beta';
import { DownloadTemplatesPage } from '@/pages/download-templates';
import { KnowledgeManagementPage } from '@/pages/knowledge';
import { KnowledgeSetsPage } from '@/pages/knowledge-sets';
import { KmEntitiesPage } from '@/pages/km-entities';
import { KnowledgeSourcesPage } from '@/pages/knowledgeSources';
import { OntologyPage } from '@/pages/ontology';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ProfilePage } from '@/pages/profile';
import { RecordsPage } from '@/pages/records';
import { RolesPage } from '@/pages/roles';
import { SamplesPage } from '@/pages/samples';
import { StopWordsPage } from '@/pages/stopWords';
import { TermsPage } from '@/pages/terms';
import { ThemesPage } from '@/pages/themes';
import { AnonymizerConfigPage } from '@/pages/anonymizer-config';
import { EmployeesPage, EmployeeGroupsPage } from '@/pages/employees';
import { IntegrationsAllPage } from '@/pages/integrations/all';
import { IntegrationDetailPage } from '@/pages/integrations/detail';
import { useIntegrationsMenuItems } from '@/widgets/IntegrationsDynamicMenu';
import { useDialogPresetMenuItems } from '@/widgets/DialogPresetsDynamicMenu';
import { useDashboardPresetMenuItems } from '@/widgets/DashboardPresetsDynamicMenu';
import { UsersPage, UserGroupsPage, UserUrlLogPage } from '@/pages/users';
import { PipelinesPage } from '@/pages/pipelines';
import { RunsPage } from '@/pages/runs';

export const appRoutes = [
  // КОНТРОЛЬ
  {
    type: 'group',
    label: ({ i18n }) => i18n.t('common:routes.control'),
    navbar: {
      icon: <ShieldCheckIcon strokeWidth={1.25} />,
    },
    children: [
      {
        path: 'quality-dashboard',
        element: <QualityDashboardPage />,
        label: ({ i18n }) => i18n.t('common:routes.qualityDashboard'),
        navbar: {
          path: '/quality-dashboard',
          icon: <ClipboardCheckIcon strokeWidth={1.25} />,
          // Закреплённые дашборды стоят сразу под «Дашбордами» — рядом с разделом, к которому относятся.
          dynamicItems: () => useDashboardPresetMenuItems(),
        },
      },
      {
        // Демонстрационные дашборды: пока бета-функция выключена, пунктов меню нет и адреса недоступны.
        path: 'renessans-dashboard',
        element: <RenessansDashboardPage />,
        label: ({ i18n }) => i18n.t('common:routes.renessansDashboard'),
        betaFeature: 'DEMO_DASHBOARDS',
        navbar: {
          path: '/renessans-dashboard',
          icon: <ChartLineIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'misselling-dashboard',
        element: <MissellingDashboardPage />,
        label: ({ i18n }) => i18n.t('common:routes.missellingDashboard'),
        betaFeature: 'DEMO_DASHBOARDS',
        navbar: {
          path: '/misselling-dashboard',
          icon: <ChartLineIcon strokeWidth={1.25} />,
        },
      },
      {
        // Адрес закреплённого дашборда-пресета: та же страница дашбордов, открытая с этим
        // пресетом. Пункта в меню у маршрута нет — его выводит DashboardPresetsDynamicMenu по
        // закреплённым пресетам. Адрес лежит вне `/quality-dashboard`, поэтому пункт
        // «Дашборды» на нём не активен.
        path: 'dashboard-preset/:slug',
        element: <QualityDashboardPage />,
        label: ({ i18n }) => i18n.t('common:routes.qualityDashboard'),
      },
      {
        path: 'dialogs/:id?',
        element: <DialogsBetaPage />,
        label: ({ i18n }) => i18n.t('common:routes.dialogs'),
        viewPermission: 'VIEW_CONVERSATION',
        navbar: {
          path: '/dialogs',
          icon: <MessageSquareTextIcon strokeWidth={1.25} />,
          // Закреплённые пресеты диалогов стоят сразу под «Диалогами».
          dynamicItems: () => useDialogPresetMenuItems(),
        },
        layout: {
          noLimitedWidth: true,
        },
      },
      {
        // Адрес закреплённого пресета: тот же список диалогов, открытый по сохранённому отбору.
        // Пункта в меню у маршрута нет — его выводит DialogPresetsDynamicMenu по закреплённым
        // пресетам. Адрес лежит вне группы `/dialogs`, поэтому пункт «Диалоги» на нём не активен.
        // Запись различается тем же необязательным сегментом, что и в разделе «Диалоги»:
        // работа с записью не выводит из раздела пресета.
        path: 'preset/:slug/:id?',
        element: <DialogsBetaPage />,
        label: ({ i18n }) => i18n.t('common:routes.dialogs'),
        viewPermission: 'VIEW_CONVERSATION',
        layout: {
          noLimitedWidth: true,
        },
      },
      // {
      //   path: 'dialogs-beta/:id?',
      //   element: <DialogsBetaPage />,
      //   label: ({ i18n }) => i18n.t('common:routes.dialogs'),
      //   viewPermission: 'DEVELOPMENT',
      //   navbar: {
      //     path: '/dialogs-beta',
      //     icon: <MessageSquareTextIcon strokeWidth={1.25} />,
      //   },
      //   layout: {
      //     noLimitedWidth: true,
      //   },
      // },
    ],
  },
  // ИНТЕГРАЦИИ
  {
    type: 'group',
    label: ({ i18n }) => i18n.t('common:routes.integrations'),
    navbar: {
      icon: <LinkIcon strokeWidth={1.25} />,
      // Динамические пункты активных интеграций под группой «Интеграции».
      dynamicItems: () => useIntegrationsMenuItems(),
    },
    children: [
      {
        path: 'records',
        element: <RecordsPage />,
        label: ({ i18n }) => i18n.t('common:routes.records'),
        viewPermission: 'ADMIN_CONVERSATION',
        navbar: {
          path: '/records',
          icon: <VoicemailIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'integrations/list',
        element: <IntegrationsAllPage />,
        label: ({ i18n }) => i18n.t('integrations:routes.all'),
        viewPermission: 'VIEW_INTEGRATION_INFO',
        navbar: {
          path: '/integrations/list',
          icon: <LinkIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'integrations/:code',
        element: <IntegrationDetailPage />,
        label: ({ i18n }) => i18n.t('integrations:routes.detail'),
        viewPermission: 'VIEW_INTEGRATION_INFO',
      },
    ],
  },
  // АНАЛИТИКА
  {
    type: 'group',
    label: ({ i18n }) => i18n.t('common:routes.analytics'),
    navbar: {
      icon: <ChartLineIcon strokeWidth={1.25} />,
    },
    children: [
      {
        path: 'behavior-factors/:id?',
        element: <BehaviorFactorsPage />,
        label: ({ i18n }) => i18n.t('common:routes.behaviorFactors'),
        viewPermission: 'VIEW_BEHAVIORAL_FACTORS',
        navbar: {
          path: '/behavior-factors',
          icon: <ChartLineIcon strokeWidth={1.25} />,
        },
      },
      {
        // Раздел выборок — тот же список диалогов, ограниченный выбранной выборкой. Выборка и
        // запись различаются необязательными сегментами: работа с записью раздел не покидает.
        path: 'samples/:sampleId?/:id?',
        element: <SamplesPage />,
        label: ({ i18n }) => i18n.t('common:routes.samples'),
        viewPermission: 'VIEW_SAMPLE',
        navbar: {
          path: '/samples',
          icon: <LayersIcon strokeWidth={1.25} />,
        },
        layout: {
          noLimitedWidth: true,
        },
      },
    ],
  },
  // СТРАТЕГ
  {
    type: 'group',
    label: ({ i18n }) => i18n.t('common:routes.strategist'),
    betaFeature: 'STRATEGIST',
    navbar: {
      icon: <NetworkIcon strokeWidth={1.25} />,
    },
    children: [
      {
        path: 'strategist/hierarchy',
        element: <StrategistHierarchyPage />,
        label: ({ i18n }) => i18n.t('common:routes.strategistHierarchy'),
        navbar: {
          path: '/strategist/hierarchy',
          icon: <NetworkIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'strategist/matrix',
        element: <StrategistMatrixPage />,
        label: ({ i18n }) => i18n.t('common:routes.strategistMatrix'),
        navbar: {
          path: '/strategist/matrix',
          icon: <Grid3x3Icon strokeWidth={1.25} />,
        },
      },
      {
        path: 'strategist/standards',
        element: <StrategistStandardsPage />,
        label: ({ i18n }) => i18n.t('common:routes.strategistStandards'),
        navbar: {
          path: '/strategist/standards',
          icon: <ClipboardListIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'strategist/transitions',
        element: <StrategistTransitionsPage />,
        label: ({ i18n }) => i18n.t('common:routes.strategistTransitions'),
        navbar: {
          path: '/strategist/transitions',
          icon: <ArrowRightLeftIcon strokeWidth={1.25} />,
        },
      },
    ],
  },
  // ЭКСПЕРТИЗА (ранее Модель знаний)
  {
    type: 'group',
    label: ({ i18n }) => i18n.t('common:routes.expertise'),
    navbar: {
      icon: <BrainCircuitIcon strokeWidth={1.25} />,
    },
    children: [
      {
        path: 'ontology/:code?',
        element: <OntologyPage />,
        label: ({ i18n }) => i18n.t('common:routes.ontology'),
        viewPermission: 'VIEW_KM_ENTITY_TYPE',
        navbar: {
          path: '/ontology',
          icon: <NetworkIcon strokeWidth={1.25} />,
        },
      },
      {
        // Наполнение модели знаний: сущности, заведённые по типам сущностей
        path: 'entities/:id?',
        element: <KmEntitiesPage />,
        label: ({ i18n }) => i18n.t('common:routes.kmEntities'),
        viewPermission: 'VIEW_KM_ENTITY_TYPE',
        navbar: {
          path: '/entities',
          icon: <BoxesIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'knowledge/:id?',
        element: <KnowledgeManagementPage />,
        label: ({ i18n }) => i18n.t('common:routes.knowledge'),
        viewPermission: 'VIEW_KNOWLEDGE',
        navbar: {
          path: '/knowledge',
          icon: <DatabaseIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'knowledge-sets/:id?',
        element: <KnowledgeSetsPage />,
        label: ({ i18n }) => i18n.t('common:routes.knowledgeSets'),
        viewPermission: 'VIEW_KNOWLEDGE',
        navbar: {
          path: '/knowledge-sets',
          icon: <LayersIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'expert-report/:reportId?/:type?/:itemId?',
        element: <ExpertReportPage />,
        label: ({ i18n }) => i18n.t('common:routes.expertReport'),
        viewPermission: 'VIEW_EXPERT_REPORT',
        navbar: {
          path: '/expert-report',
          icon: <FileCheck2Icon strokeWidth={1.25} />,
        },
      },
      {
        path: 'expert-report-feedback',
        element: <ExpertReportFeedbackPage />,
        label: ({ i18n }) => i18n.t('common:routes.expertReportFeedback'),
        viewPermission: 'VIEW_EXPERT_REPORT_FEEDBACK',
        navbar: {
          path: '/expert-report-feedback',
          icon: <FilePenLineIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'competencies/:competencyId?',
        element: <CoachingPage />,
        label: ({ i18n }) => i18n.t('common:routes.competencies'),
        viewPermission: 'VIEW_COACHING',
        navbar: {
          path: '/competencies',
          icon: <GraduationCapIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'knowledge-sources',
        element: <KnowledgeSourcesPage />,
        label: ({ i18n }) => i18n.t('common:routes.knowledgeSources'),
        viewPermission: 'VIEW_KNOWLEDGE_SOURCE',
        navbar: {
          path: '/knowledge-sources',
          icon: <FileTextIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'attributes/:id?/:parentStruct?',
        element: <AttributesPage />,
        label: ({ i18n }) => i18n.t('common:routes.attributes'),
        viewPermission: 'VIEW_ATTRIBUTE',
        navbar: {
          path: '/attributes',
          icon: <VariableIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'attribute-groups/:id?',
        element: <AttributeGroupsPage />,
        label: ({ i18n }) => i18n.t('common:routes.attributeGroups'),
        viewPermission: ['VIEW_ATTRIBUTE_GROUP', 'VIEW_ATTRIBUTE'],
        navbar: {
          path: '/attribute-groups',
          icon: <Icon type={'folder-variables'} />,
        },
      },
      {
        path: 'terms',
        element: <TermsPage />,
        label: ({ i18n }) => i18n.t('common:routes.terms'),
        viewPermission: 'VIEW_TERM',
        navbar: {
          path: '/terms',
          icon: <BookOpenIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'stop-words',
        element: <StopWordsPage />,
        label: ({ i18n }) => i18n.t('common:routes.stopWords'),
        viewPermission: 'VIEW_STOP_WORD',
        navbar: {
          path: '/stop-words',
          icon: <BookOpenIcon strokeWidth={1.25} />,
        },
      },
    ],
  },
  // СИСТЕМА
  {
    type: 'group',
    label: ({ i18n }) => i18n.t('common:routes.system'),
    navbar: {
      icon: <SettingsIcon strokeWidth={1.25} />,
    },
    children: [
      {
        path: 'pipelines/list',
        element: <PipelinesPage />,
        label: ({ i18n }) => i18n.t('common:routes.pipelines'),
        viewPermission: 'VIEW_JOBS',
        navbar: {
          path: '/pipelines/list',
          icon: <WorkflowIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'pipelines/runs',
        element: <RunsPage />,
        label: ({ i18n }) => i18n.t('common:routes.runs'),
        viewPermission: 'VIEW_JOBS',
        navbar: {
          path: '/pipelines/runs',
          icon: <HistoryIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'anonymizer-config/*',
        element: <AnonymizerConfigPage />,
        label: ({ i18n }) => i18n.t('common:routes.anonymizerConfig'),
        viewPermission: ['VIEW_ANONYMIZE_CONFIG', 'ADMIN_ANONYMIZE_CONFIG'],
        navbar: {
          path: '/anonymizer-config/fio',
          icon: <SettingsIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'themes',
        element: <ThemesPage />,
        label: ({ i18n }) => i18n.t('common:routes.themes'),
        viewPermission: 'VIEW_THEMES',
        navbar: {
          path: '/themes',
          icon: <PaletteIcon strokeWidth={1.25} />,
        },
        layout: {
          noLimitedWidth: true,
        },
      },
      {
        path: 'user-url-log',
        element: <UserUrlLogPage />,
        label: ({ i18n }) => i18n.t('common:routes.userUrlLog'),
        viewPermission: 'VIEW_USER_LOG',
        navbar: {
          path: '/user-url-log',
          icon: <HistoryIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'billing',
        element: <BillingPage />,
        label: ({ i18n }) => i18n.t('common:routes.billing'),
        viewPermission: 'VIEW_BILLING',
        navbar: {
          path: '/billing',
          icon: <ReceiptIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'download-templates',
        element: <DownloadTemplatesPage />,
        label: ({ i18n }) => i18n.t('common:routes.downloadTemplates'),
        viewPermission: 'ADMIN_DOWNLOAD_TEMPLATE',
        navbar: {
          path: '/download-templates',
          icon: <FileStackIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'general-settings',
        element: <GeneralSettingsPage />,
        label: ({ i18n }) => i18n.t('common:routes.generalSettings'),
        viewPermission: ['VIEW_GENERAL_SETTINGS', 'ADMIN_GENERAL_SETTINGS'],
        navbar: {
          path: '/general-settings',
          icon: <SlidersHorizontalIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'audio-audit',
        element: <AudioAuditPage />,
        label: ({ i18n }) => i18n.t('common:routes.audioAudit'),
        viewPermission: 'ADMIN_AUDIO_AUDIT',
        navbar: {
          path: '/audio-audit',
          icon: <AudioLinesIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'beta-features',
        element: <BetaFeaturesPage />,
        label: ({ i18n }) => i18n.t('common:routes.betaFeatures'),
        viewPermission: 'ADMIN_BETA_FEATURE',
        navbar: {
          path: '/beta-features',
          icon: <FlaskConicalIcon strokeWidth={1.25} />,
        },
      },
    ],
  },
  // ПОЛЬЗОВАТЕЛИ (в конце)
  {
    type: 'group',
    label: ({ i18n }) => i18n.t('common:routes.users'),
    navbar: {
      icon: <UsersIcon strokeWidth={1.25} />,
    },
    children: [
      {
        path: 'users/lists/:id?',
        element: <UsersPage />,
        label: ({ i18n }) => i18n.t('common:routes.users'),
        viewPermission: ['VIEW_USER'],
        navbar: {
          icon: <UsersIcon strokeWidth={1.25} />,
          path: '/users/lists',
        },
      },
      {
        path: 'roles',
        element: <RolesPage />,
        label: ({ i18n }) => i18n.t('common:routes.roles'),
        viewPermission: 'VIEW_ROLE',
        navbar: {
          path: '/roles',
          icon: <MonitorXIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'user-groups/:id?',
        element: <UserGroupsPage />,
        label: ({ i18n }) => i18n.t('common:routes.usersGroups'),
        viewPermission: ['VIEW_USER_GROUP'],
        navbar: {
          icon: <Icon type={'folder-users'} />,
          path: 'user-groups',
        },
      },
      {
        path: 'employees/:id?',
        element: <EmployeesPage />,
        label: ({ i18n }) => i18n.t('common:routes.employees'),
        viewPermission: ['VIEW_EMPLOYEE', 'ADMIN_EMPLOYEE'],
        navbar: {
          path: '/employees',
          icon: <HeadphonesIcon strokeWidth={1.25} />,
        },
      },
      {
        path: 'employee-groups/:id?',
        element: <EmployeeGroupsPage />,
        label: ({ i18n }) => i18n.t('common:routes.employeeGroups'),
        viewPermission: ['VIEW_EMPLOYEE_GROUP'],
        navbar: {
          path: '/employee-groups',
          icon: <Icon type={'folder-users'} />,
        },
      },
    ],
  },
  {
    path: 'profile',
    element: <ProfilePage />,
    label: ({ i18n }) => i18n.t('common:routes.profile'),
  },
] as const satisfies RouteConfig[];

export const rootRoutes = [
  {
    path: '/',
    element: <AppLayout menuItems={appRoutes} />,
    // Экран восстановления при падении самого каркаса: показывать меню и верхнюю панель не из
    // чего, поэтому он занимает всё окно. Падения страниц перехватываются их собственными
    // маршрутами (см. AppRouter) и каркас не снимают. Перехват вокруг всего приложения
    // (ErrorBoundary в App) остаётся для ошибок вне маршрутов.
    errorElement: <RouteErrorElement />,
    label: () => '',
    getDefaultPath: () => {
      return '/quality-dashboard';
    },
    children: [
      {
        path: '*',
        label: () => '',
        element: <NotFoundPage />,
      },
      ...appRoutes,
    ],
  },
] as const satisfies RouteConfig[];
